const TRACKJS_DOMAIN = "trackjs.com"
const SPLINE_CHUNK_PATH = /\/assets\/react-spline-[^/]+\.js$/

const isTrackJSOrigin = (source: string): boolean => {
  try {
    const { hostname } = new URL(source)
    return hostname === TRACKJS_DOMAIN || hostname.endsWith(`.${TRACKJS_DOMAIN}`)
  } catch {
    return false
  }
}

const blockedSource = (blockedURI: string): string => {
  if (!blockedURI) return "unknown"

  // Browsers use values such as "inline" and "eval" when no URL was blocked.
  if (/^[a-z-]+$/i.test(blockedURI)) return blockedURI

  try {
    const url = new URL(blockedURI, document.baseURI)
    if (["http:", "https:", "ws:", "wss:"].includes(url.protocol)) return url.origin
    return url.protocol
  } catch {
    return "unknown"
  }
}

const sourceFileOrigin = (sourceFile: string): string | undefined => {
  if (!sourceFile) return undefined

  try {
    const url = new URL(sourceFile, document.baseURI)
    return url.origin === "null" ? url.protocol : url.origin
  } catch {
    return undefined
  }
}

const resourcePathname = (resource: string): string | undefined => {
  if (!resource || /^[a-z-]+$/i.test(resource)) return undefined

  try {
    const url = new URL(resource, document.baseURI)
    if (!["http:", "https:", "ws:", "wss:"].includes(url.protocol)) return undefined
    return url.pathname
  } catch {
    return undefined
  }
}

// Spline probes eval support and gracefully falls back when CSP blocks it.
const isSplineEvalViolation = (event: SecurityPolicyViolationEvent): boolean => {
  if (event.blockedURI !== "eval" || event.effectiveDirective !== "script-src") return false

  try {
    return SPLINE_CHUNK_PATH.test(new URL(event.sourceFile, document.baseURI).pathname)
  } catch {
    return false
  }
}

type CSPViolationMetadata = Record<string, string>

const violationSample = (sample: string): string | undefined => {
  if (!sample) return undefined
  const normalized = sample.replace(/\s+/g, " ").trim()
  return normalized || undefined
}

/** Report each distinct CSP violation to TrackJS once per page load. */
export const installCSPViolationReporting = (
  track: (message: string, metadata: CSPViolationMetadata) => void
): (() => void) => {
  // Bytespider injects third-party scripts that our CSP correctly blocks, creating noisy reports.
  if (/\bBytespider\b/i.test(navigator.userAgent)) return () => undefined

  const reportedViolations = new Set<string>()

  const onViolation = (event: SecurityPolicyViolationEvent) => {
    if (isSplineEvalViolation(event)) return

    const source = blockedSource(event.blockedURI)
    const sourceOrigin = sourceFileOrigin(event.sourceFile)
    const sample = violationSample(event.sample)
    const metadata: CSPViolationMetadata = {}

    const blockedPath = resourcePathname(event.blockedURI)
    const sourcePath = resourcePathname(event.sourceFile)
    if (blockedPath) metadata["CSP Blocked Path"] = blockedPath
    if (sourcePath) metadata["CSP Source Path"] = sourcePath
    if (event.lineNumber > 0) metadata["CSP Source Line"] = String(event.lineNumber)
    if (event.columnNumber > 0) metadata["CSP Source Column"] = String(event.columnNumber)
    if (sample) metadata["CSP Sample"] = sample

    // If CSP ever blocks TrackJS itself, attempting to report that failure could recurse.
    if (isTrackJSOrigin(source)) return

    const sourceContext = sourceOrigin ? ` from ${sourceOrigin}` : ""
    const message = `CSP violation [${event.disposition}]: ${event.effectiveDirective} blocked ${source}${sourceContext}`
    const reportKey = JSON.stringify([message, Object.entries(metadata).sort()])
    if (reportedViolations.has(reportKey)) return

    reportedViolations.add(reportKey)
    track(message, metadata)
  }

  document.addEventListener("securitypolicyviolation", onViolation)
  return () => document.removeEventListener("securitypolicyviolation", onViolation)
}
